/**
 * Thrown when interactive authentication is required (session expired or no account).
 * Callers (React Query, etc.) can check `instanceof` to suppress retries.
 */
export class AuthRequiredError extends Error {
  override name = 'AuthRequiredError';
  constructor(message = 'Authentication required — interactive sign-in needed') {
    super(message);
  }
}

/**
 * Thrown when a requested resource doesn't exist (HTTP 404) — e.g. a
 * historical usage period or user with no archived data. Callers should
 * catch this and render an empty state rather than a raw error banner.
 */
export class NotFoundError extends Error {
  override name = 'NotFoundError';
  constructor(message = 'The requested resource was not found') {
    super(message);
  }
}

/**
 * Safely extract a human-readable error message from an unknown value.
 * Handles Error instances, strings, objects with `.message`, and fallbacks.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  if (error !== null && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown error';
    }
  }
  return 'Unknown error';
}
